import { Form, Formik } from 'formik';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Row,
} from 'react-bootstrap';
import PropTypes from 'prop-types';
import InputFormGroup from './util/InputFormGroup';
import { isEmbargoedCountry } from './util/dataHelpers';
import { getLogger } from '../util/logger';
import useRequireAuth from '../hooks/useRequireAuth';
import useTheme from './hooks/useTheme';
import ProgressModal from './util/ProgressModal';
import { useRequestTrialFromHandlers } from './hooks/useRequestTrialFromHandlers';

const logger = getLogger('RequestTrialForm');

function RequestTrialForm({ onComplete, onCancel }) {
  const data = useRequireAuth();
  const [theme] = useTheme();
  const {
    handleSubmit,
    handleCancel,
    errorMsg,
    showProgress,
    showAlert,
    setShowAlert,
    alert,
  } = useRequestTrialFromHandlers(onComplete, onCancel);

  if (!data?.tenant) {
    return null;
  }

  if (data?.trialRequested) {
    return (
      <>
        <br />A trial has been requested for your tenant, and you should hear
        back soon. Please reach out to RealConnect_Trial@poly.com for further
        questions.
      </>
    );
  }

  const hasAnyLicenses =
    data?.tenant?.internalLicenses?.length > 0 ||
    data?.tenant?.externalLicenses?.licenses?.length > 0;

  if (hasAnyLicenses) {
    return (
      <>
        <br />
        Your organization has already had a RealConnect license, and is not
        eligible for a trial. To renew licenses, please contact
        RealConnect_Trial@poly.com
      </>
    );
  }

  if (isEmbargoedCountry(data?.tenant?.countryCode)) {
    return (
      <>
        <br />
        Your organization is in an embargoed country [{data.tenant.countryCode}]
        and is not eligible for a trial.
      </>
    );
  }

  const { organization, user } = data;

  if (!data.organization) {
    logger.error('no organization found');
    return null;
  }

  if (!data.user) {
    logger.error('no user found');
    return null;
  }

  const userBusinessPhone =
    user?.businessPhones?.length > 0 && user.businessPhones[0];

  return (
    <>
      <Alert
        variant="danger"
        show={showAlert}
        onClose={() => setShowAlert(false)}
        dismissible
      >
        <Alert.Heading>{alert.title}</Alert.Heading>
        <p>{alert.message}</p>
      </Alert>
      <ProgressModal show={showProgress} message="Requesting trial..." />
      <Badge variant="danger">{errorMsg}</Badge>
      <p />
      <h1>Trial</h1>
      Connect your organization to Microsoft Teams today at no charge for 60
      days with Poly RealConnect Service.
      <p />
      Poly RealConnect Service for Teams is a cloud-based video interoperability
      service that provides connectivity from H.323/SIP-based video endpoints to
      Microsoft Teams meetings. (Skype for Business meeting is also supported).
      In this 60-day free trial, you will be able to connect up to five
      videoconferencing endpoints to Teams meetings (5 concurrent connections).
      <p />
      <b>
        <u>PROGRAM TERMS AND CONDITIONS</u>
      </b>
      <ol>
        <li>
          To make calls to the RealConnect Service over the internet, video
          endpoints must be able to traverse firewall endpoints to establish
          connection and receive media. The RealConnect Service does not include
          a firewall traversal solution. Please see RealConnect Service
          Description for more information regarding the service
        </li>
        <li>
          This promotion is available in regions where RealConnect for Microsoft
          Teams is offered, provided that this offer is not available in certain
          countries including China, Russia, and any country subject to US,
          Singapore or European Union embargoes or sanctions
        </li>
        <li>
          This program is subject to the Poly Sales Promotion Terms available{' '}
          <a href="https://www.poly.com/content/dam/www/legal/doc/poly-sales-promotion-terms-en-v190606.pdf">
            here
          </a>{' '}
          and incorporated herein. Poly RealConnect Service is subject to the
          Service Description for such product. In the event of a conflict
          between this program, the applicable Service Description, and the Poly
          Sales Promotion Terms, the Poly Sales Promotion Terms shall take
          precedence, followed by the Service Description, and then this program
          document
        </li>
        <li>
          This program is void where prohibited or otherwise restricted by law.
          Polycom reserves the right to modify or cancel this program at any
          time. Polycom has the right to deny all claims that do not meet the
          criteria outlined in the program terms and conditions as set forth in
          the Service Description
        </li>
      </ol>
      <u>
        <a
          target="#"
          href="https://www.poly.com/content/dam/www/legal/doc/poly-sales-promotion-terms-en-v190606.pdf"
        >
          TERMS &amp; CONDITIONS
        </a>
      </u>
      <p />
      <b>Trial licenses will be associated with the following information:</b>
      <p />
      <Card style={{ border: 0, width: '570px' }}>
        <Card.Body>
          <Card.Header>Organization</Card.Header>
          <Container style={{ padding: 10 }}>
            <Row>
              <Col xs={3}>Company Name:</Col>
              <Col>{organization.displayName}</Col>
            </Row>
            <Row>
              <Col xs={3}>Microsoft ID:</Col>
              <Col>{organization.id}</Col>
            </Row>
            <Row>
              <Col xs={3}>Street:</Col>
              <Col>{organization.street}</Col>
            </Row>
            <Row>
              <Col xs={3}>City:</Col>
              <Col>{organization.city}</Col>
            </Row>
            <Row>
              <Col xs={3}>State:</Col>
              <Col>{organization.state}</Col>
            </Row>
            <Row>
              <Col xs={3}>Postal Code:</Col>
              <Col>{organization.postalCode}</Col>
            </Row>
            <Row>
              <Col xs={3}>Country:</Col>
              <Col>{organization.countryLetterCode}</Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>
      <Card style={{ border: 0, width: '570px' }}>
        <Card.Body>
          <Card.Header>User</Card.Header>
          <Container style={{ padding: 10 }}>
            <Row>
              <Col xs={3}>Name:</Col>
              <Col>
                {user.givenName} {user.surname}
              </Col>
            </Row>
            <Row>
              <Col xs={3}>Job Title:</Col>
              <Col>{user.jobTitle}</Col>
            </Row>
            <Row>
              <Col xs={3}>Phone:</Col>
              <Col>{userBusinessPhone}</Col>
            </Row>
            <Row>
              <Col xs={3}>Email:</Col>
              <Col>{user.mail}</Col>
            </Row>
          </Container>
        </Card.Body>
      </Card>
      <br />
      <Formik
        initialValues={{
          tenantId: organization.id || '',
          tenantName: organization.displayName || '',
          companyName: organization.displayName || '',
          country: organization.countryLetterCode || '',
          streetAddress: organization.street || '',
          streetAddress2: '',
          city: organization.city || '',
          postalCode: organization.postalCode || '',
          emailAddress: user.mail || '',
          firstName: user.givenName || '',
          lastName: user.surname || '',
          workPhone: userBusinessPhone,
          jobTitle: user.jobTitle || '',
          preferredReseller: '',
          emailOptIn: false,
        }}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <InputFormGroup
              name="preferredReseller"
              label="Preferred Reseller:"
            />
            <Button
              style={{ marginRight: '.15em' }}
              type="submit"
              variant={theme?.buttonVariant}
              size="lg"
              disabled={isSubmitting}
            >
              SUBMIT
            </Button>
            <Button
              variant={theme?.buttonVariant}
              size="lg"
              onClick={handleCancel}
            >
              CANCEL
            </Button>
          </Form>
        )}
      </Formik>
    </>
  );
}

RequestTrialForm.propTypes = {
  onComplete: PropTypes.func,
  onCancel: PropTypes.func,
};

export default RequestTrialForm;
